import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { buildBreadcrumbScript } from "@/lib/serviceHubSchema";

const SITE_URL = "https://coloradowebimpressions.com";
const LAST_UPDATED = "May 11, 2026";

export const Route = createFileRoute("/sms-policy")({
  head: () => ({
    meta: [
      { title: "SMS / Text Messaging Policy | Colorado Web Impressions" },
      {
        name: "description",
        content:
          "How Colorado Web Impressions uses SMS and text messages. Opt-in, opt-out (STOP), help (HELP), message frequency, and carrier disclosures.",
      },
      { name: "robots", content: "index, follow" },
      { property: "og:title", content: "SMS / Text Messaging Policy | Colorado Web Impressions" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/sms-policy` }],
    scripts: [
      buildBreadcrumbScript([
        { name: "Home", path: "/" },
        { name: "SMS Policy", path: "/sms-policy" },
      ]),
    ],
  }),
  component: SmsPolicyPage,
});

function SmsPolicyPage() {
  return (
    <SiteLayout>
      <PageHero
        eyebrow="Legal"
        title="SMS / Text Messaging Policy"
        description={`Last updated: ${LAST_UPDATED}. Plain-English rules for the text messages we send from Colorado Web Impressions.`}
      />

      <section className="bg-background py-16 lg:py-20">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <article className="prose-article">
            <h2>Program description</h2>
            <p>
              Colorado Web Impressions sends transactional and conversational
              SMS messages to people who have given us their phone number and
              checked the SMS consent box on our contact or proposal forms, or
              who have texted us first. Messages relate to:
            </p>
            <ul>
              <li>Replies to your inquiry or quote request</li>
              <li>Appointment scheduling and reminders</li>
              <li>Project updates while you are an active client</li>
              <li>Occasional account or billing notices</li>
            </ul>
            <p>
              We do <strong>not</strong> send marketing blasts, bulk promotional
              campaigns, or third-party offers via SMS.
            </p>

            <h2>How to opt in</h2>
            <p>You opt in by either:</p>
            <ul>
              <li>
                Checking the consent box on our{" "}
                <Link to="/contact" className="font-bold text-brand-gold-deep underline">
                  contact form
                </Link>{" "}
                or proposal form, which states you agree to receive emails, calls,
                and SMS from Colorado Web Impressions, or
              </li>
              <li>
                Texting <strong>START</strong> to our business number after
                previously opting out.
              </li>
            </ul>

            <h2>Message frequency</h2>
            <p>
              Message frequency varies based on your engagement with us. Most
              people receive fewer than 10 messages per month.
            </p>

            <h2>Message and data rates</h2>
            <p>
              <strong>Message and data rates may apply</strong> depending on
              your wireless carrier and plan. We do not charge you anything to
              send or receive these messages; any cost is from your carrier.
            </p>

            <h2>Opt-out and help</h2>
            <ul>
              <li>
                Reply <strong>STOP</strong> to any of our messages at any time to
                unsubscribe. You will receive one confirmation message and then
                no further texts.
              </li>
              <li>
                Reply <strong>HELP</strong> for help, or contact us directly at{" "}
                <a
                  href="tel:+17194193935"
                  className="font-bold text-brand-gold-deep underline"
                 aria-label="Call (719) 419-3935">
                  (719) 419-3935
                </a>{" "}
                or{" "}
                <a
                  href="mailto:info@coloradowebimpressions.com"
                  className="font-bold text-brand-gold-deep underline"
                 aria-label="Email Colorado Web Impressions">
                  info@coloradowebimpressions.com
                </a>
                .
              </li>
            </ul>

            <h2>Supported carriers</h2>
            <p>
              We support all major US carriers, including AT&amp;T, Verizon,
              T-Mobile, Sprint, US Cellular, and most regional carriers. Carriers
              are not liable for delayed or undelivered messages.
            </p>

            <h2>Privacy</h2>
            <p>
              Phone numbers and SMS opt-in information are <strong>never sold,
              rented, or shared with third parties</strong> for marketing
              purposes. Mobile information is only shared with the messaging
              service provider strictly necessary to deliver your messages.
              See our{" "}
              <Link to="/privacy" className="font-bold text-brand-gold-deep underline">
                how we handle your data
              </Link>{" "}
              for full details on how we handle your information.
            </p>

            <h2>Changes to this policy</h2>
            <p>
              When we update this policy, we will change the &ldquo;Last
              updated&rdquo; date at the top. Material changes will be
              highlighted for at least 30 days.
            </p>

            <h2>Contact</h2>
            <p>
              Questions? Email{" "}
              <a
                href="mailto:info@coloradowebimpressions.com"
                className="font-bold text-brand-gold-deep underline"
               aria-label="Email Colorado Web Impressions">
                info@coloradowebimpressions.com
              </a>{" "}
              or call{" "}
              <a href="tel:+17194193935" className="font-bold text-brand-gold-deep underline" aria-label="Call (719) 419-3935">
                (719) 419-3935
              </a>
              .
            </p>
          </article>
        </div>
      </section>
    </SiteLayout>
  );
}
